import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useTypedSelector } from "../../../../store/store";
import Checkbox from "../../../shared/Checkbox";

import styles from "./index.module.scss";

type PriceTypeValue = "deposit" | "full" | string;

interface IPriceTypeProps {
  full: number;
  deposit: number;
  currency: string;
  priceType?: PriceTypeValue;
  onPriceTypeChange: (priceType: PriceTypeValue) => void;
}

function PriceType({
  full,
  deposit,
  currency,
  priceType: externalPriceType,
  onPriceTypeChange,
}: IPriceTypeProps) {
  const { t } = useTranslation();

  const { api_language, payment_option_text } = useTypedSelector(
    (state) => state.environment,
  );

  const [internalPriceType, setInternalPriceType] =
    useState<PriceTypeValue>("deposit");

  const currentPriceType = externalPriceType ?? internalPriceType;

  useEffect(() => {
    if (externalPriceType && externalPriceType !== internalPriceType) {
      setInternalPriceType(externalPriceType);
    }
  }, [externalPriceType, internalPriceType]);

  const { formattedFull, formattedDeposit } = useMemo(() => {
    const formatter = new Intl.NumberFormat(api_language || "en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    });

    return {
      formattedFull: formatter.format(full),
      formattedDeposit:
        deposit === 0 || full === 0 ? "N/A" : formatter.format(deposit),
    };
  }, [full, deposit, currency]);

  const handlePriceTypeChange = (type: PriceTypeValue) => () => {
    if (type === "deposit" && formattedDeposit === "N/A") {
      return;
    }

    setInternalPriceType(type);
    onPriceTypeChange(type);
  };

  return (
    <div className={styles.container}>
      {formattedDeposit !== formattedFull && (
        <div
          onClick={handlePriceTypeChange("deposit")}
          className={classNames(styles.item, {
            [styles.item_active]: currentPriceType === "deposit",
            [styles.item_disabled]: formattedDeposit === "N/A",
          })}>
          <div className={styles.priceInfo}>
            <p className={styles.title}>{t("deposit payment")}</p>
            <p className={styles.price}>{formattedDeposit}</p>
          </div>

          <div className={styles.checkboxContainer}>
            <Checkbox
              value={currentPriceType === "deposit"}
              onChange={handlePriceTypeChange("deposit")}
            />
          </div>
        </div>
      )}

      <div
        onClick={handlePriceTypeChange("full")}
        className={classNames(styles.item, {
          [styles.item_active]: currentPriceType === "full",
        })}>
        <div className={styles.priceInfo}>
          <p className={styles.title}>{t("full amount")}</p>
          <p className={styles.price}>{formattedFull}</p>
        </div>

        <div className={styles.checkboxContainer}>
          <Checkbox
            value={currentPriceType === "full"}
            onChange={handlePriceTypeChange("full")}
          />
        </div>
      </div>

      {payment_option_text ? (
        <div
          onClick={handlePriceTypeChange(payment_option_text)}
          className={classNames(styles.item)}>
          <div className={styles.priceInfo}>
            <p className={styles.title}>{payment_option_text}</p>
          </div>

          <div className={styles.checkboxContainer}>
            <Checkbox
              value={currentPriceType === payment_option_text}
              onChange={handlePriceTypeChange(payment_option_text)}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default PriceType;
